import { EventEmitter }              from '@angular/core';
import { ArchivingImportStatusEnum } from '@app/modules/archiving/models/archiving-import.model';
import { APP_CONFIG }                from '@appConfig/_app-config_master';
import { DeviceTypeEnum }            from '@appConfig/media-config';
import { ITabContent }               from '@core/interfaces/common.interfaces';
import { SortOrderEnum }             from '@shared/list-operations/components/sort-filter/sort-filter.model';
import { LIST_OPERATIONS_METADATA }  from '@shared/list-operations/config/list-operations.config';
import {
	FilterIdentifiersEnum,
	FilterTypes
}                                    from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	ListOperationsIdentifierEnum
}                                    from '@shared/list-operations/model/list-operations.model';
import { PagingModeEnum }            from '@shared/list-operations/model/paging.model';

interface IArchivingImportMetadata {

	entityId: string;

	storeId: string;

	entity: {
		dateFormat: Intl.DateTimeFormatOptions;
	};

	details: {
		dateFormat: Intl.DateTimeFormatOptions;
		internalTabContent: ITabContent[];
	};

	listOperations: IListOperationsConfig;

	i18n: {
		basepath: string;
		page: string;
		entity: string;
		details: string;
	};
}


export const ARCHIVING_IMPORT_METADATA: IArchivingImportMetadata = {
	entityId: APP_CONFIG.archiving.entityId,
	storeId: APP_CONFIG.archiving.store.id,
	entity: {
		dateFormat: { day: '2-digit', month: 'long', year: 'numeric' }
	},
	details: {
		dateFormat: { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
		internalTabContent: [
			{
				key: 'id',
				order: 1,
				i18nPath: 'archiving.page.details.importId',
				addSpaceToPrevious: false,
				copyClipboard: true,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ]
			},
			{
				key: 'fingerprint.algorithm',
				order: 2,
				i18nPath: 'archiving.page.details.algorithm',
				addSpaceToPrevious: true,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ]
			},
			{
				key: 'fingerprint.hash',
				order: 3,
				i18nPath: 'archiving.page.details.hash',
				addSpaceToPrevious: false,
				copyClipboard: true,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ]
			}
		]
	},
	listOperations: {
		id: ListOperationsIdentifierEnum.BUSINESS_PREMISES_DETAIL_PAGE_ARCHIVE_IMPORTS,
		pagingData: {
			mode: PagingModeEnum.Light,
			continuesPagesMode: false,
			defaultValues: {
				pageSize: LIST_OPERATIONS_METADATA.default.paging.pageSize ?? 20,
				pageNumber: LIST_OPERATIONS_METADATA.default.paging.pageNumber ?? 0
			}
		},
		searchData: {
			placeholderI18nPath: 'archiving.search.placeholder'
		},
		sortData: [
			{
				name: 'created',
				i18nPath: 'shared.forms.created.label',
				defaultActive: true,
				sortOrder: SortOrderEnum.DESC
			},
			{
				name: 'status',
				i18nPath: 'shared.forms.status.label'
			}
		],
		filterData: [
			{
				order: 1,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.BUSINESS_DATE,
				type: FilterTypes.FILTER_DATE_RANGE_PICKER_TYPE,
				i18nPath: 'archiving.filter.businessDate',
				service: 'businessDate',
				// TODO rename prefix to a more context specific name (its used to prefix id on date filters)
				prefix: 'imports_',
				properties: {
					dateInputFormat: { year: 'numeric', month: 'short', day: '2-digit' }
				}
			},
			{
				order: 2,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.CREATED_DATE,
				type: FilterTypes.FILTER_DATE_RANGE_PICKER_TYPE,
				i18nPath: 'archiving.filter.created',
				service: 'createdDate',
				// TODO rename prefix to a more context specific name (its used to prefix id on date filters)
				prefix: 'imports#2_',
				properties: {
					dateInputFormat: { year: 'numeric', month: 'short', day: '2-digit' }
				}
			},
			{
				order: 3,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.ERS_ID,
				type: FilterTypes.FILTER_SELECT_ARCHIVING_ERS_TYPE,
				selectFilter: {
					paramKey: 'id',
					searchRestoreValueKey: 'id'
				},
				i18nPath: 'archiving.filter.ers',
				service: 'archivingImport',
				bindLabel: 'ersIdentifier',
				bindValue: 'ersIdentifier',
				resetEventEmitter: new EventEmitter<boolean>(),
				values: [],
				items: [],
				tooltip: {
					bindKey: 'ersIdentifier'
				}
			},
			{
				order: 4,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.STATUS,
				type: FilterTypes.FILTER_LIST_TYPE,
				i18nPath: 'archiving.filter.status',
				items: [
					{ value: ArchivingImportStatusEnum.LOCKED, i18nPath: 'archiving.enums.status.locked' },
					{ value: ArchivingImportStatusEnum.OPEN, i18nPath: 'archiving.enums.status.open' },
					{ value: ArchivingImportStatusEnum.REPLACED, i18nPath: 'archiving.enums.status.replaced' },
					{ value: ArchivingImportStatusEnum.BEING_REPLACED, i18nPath: 'archiving.enums.status.being_replaced' },
					{ value: ArchivingImportStatusEnum.ERROR, i18nPath: 'archiving.enums.status.error' }
				]
			}
		]
	},
	i18n: {
		basepath: APP_CONFIG.archiving.i18nPath,
		page: 'page',
		entity: 'entity',
		details: 'details'
	}
};
